<template>
  <div class="obra-detalle">
    <div class="content-header">
      <div class="content-header-left">
        <h1 class="content-header-title">Detalle de Obra</h1>
        <p class="content-header-subtitle">Información completa de la obra</p>
      </div>
      <div class="content-header-right">
        <b-button variant="outline-secondary" @click="volver" class="mr-2">
          <feather-icon icon="ArrowLeftIcon" size="16" class="mr-50" />
          Volver
        </b-button>
        <b-button variant="primary" @click="editarObra">
          <feather-icon icon="EditIcon" size="16" class="mr-50" />
          Editar
        </b-button>
      </div>
    </div>

    <!-- Indicador de carga -->
    <div v-if="cargando" class="text-center py-4">
      <b-spinner variant="primary" label="Cargando obra..."></b-spinner>
      <p class="mt-2 text-muted">Cargando información de la obra...</p>
    </div>

    <!-- Contenido de la obra -->
    <div v-else-if="obra" class="row">
      <!-- Información general -->
      <div class="col-md-8">
        <b-card title="Información General" class="mb-4">
          <b-row>
            <b-col md="6">
              <b-form-group label="Dirección">
                <p class="form-control-plaintext">{{ obra.direccion }}</p>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Referencia Externa">
                <p class="form-control-plaintext">{{ obra.referenciaExterna || 'Sin referencia' }}</p>
              </b-form-group>
            </b-col>
          </b-row>
          
          <b-row>
            <b-col md="4">
              <b-form-group label="Municipio">
                <p class="form-control-plaintext">{{ obra.municipio }}</p>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Provincia">
                <p class="form-control-plaintext">{{ obra.provincia }}</p>
              </b-form-group>
            </b-col>
            <b-col md="4">
              <b-form-group label="Código Postal">
                <p class="form-control-plaintext">{{ obra.codigoPostal }}</p>
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group label="Descripción">
            <p class="form-control-plaintext">{{ obra.descripcion || 'Sin descripción' }}</p>
          </b-form-group>
        </b-card>

        <!-- Información de la obra -->
        <b-card title="Detalles de la Obra" class="mb-4">
          <b-row>
            <b-col md="6">
              <b-form-group label="Tipo de Obra">
                <b-badge variant="info" class="p-2">
                  {{ obra.nombreTipoObra || 'Sin tipo' }}
                </b-badge>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Estado">
                <b-badge :variant="getEstadoVariant(obra.nombreEstadoObra)" class="p-2">
                  {{ obra.nombreEstadoObra || 'Sin estado' }}
                </b-badge>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <b-form-group label="Fase">
                <b-badge variant="secondary" class="p-2">
                  {{ obra.nombreFase || 'Sin fase' }}
                </b-badge>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Referencia de Obra">
                <p class="form-control-plaintext">{{ obra.nombreReferenciaObra || 'Sin referencia' }}</p>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <b-form-group label="Fecha de Inicio">
                <p class="form-control-plaintext">{{ formatearFecha(obra.fechaInicioObra) }}</p>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group label="Fecha de Fin">
                <p class="form-control-plaintext">{{ formatearFecha(obra.fechaFinObra) }}</p>
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group label="Magnitudes">
            <p class="form-control-plaintext">{{ obra.magnitudes || 'Sin magnitudes' }}</p>
          </b-form-group>
        </b-card>
      </div>

      <!-- Información de empresa y propietario -->
      <div class="col-md-4">
        <b-card title="Empresa" class="mb-4">
          <b-form-group label="Nombre">
            <p class="form-control-plaintext">{{ obra.nombreEmpresa || 'Sin empresa' }}</p>
          </b-form-group>
        </b-card>

        <b-card title="Propietario" class="mb-4">
          <b-form-group label="Nombre">
            <p class="form-control-plaintext">{{ obra.nombreOwner || 'Sin propietario' }}</p>
          </b-form-group>
        </b-card>

        <b-card title="Fechas del Sistema">
          <b-form-group label="Creado">
            <p class="form-control-plaintext">{{ formatearFechaHora(obra.createdAt) }}</p>
          </b-form-group>
          <b-form-group label="Última Actualización">
            <p class="form-control-plaintext">{{ formatearFechaHora(obra.updatedAt) }}</p>
          </b-form-group>
        </b-card>
      </div>
    </div>

    <!-- Error si no se encuentra la obra -->
    <div v-else class="text-center py-5">
      <feather-icon icon="AlertCircleIcon" size="48" class="text-warning mb-3"></feather-icon>
      <h4>Obra no encontrada</h4>
      <p class="text-muted">La obra que buscas no existe o ha sido eliminada.</p>
      <b-button variant="primary" @click="volver">
        Volver al listado
      </b-button>
    </div>
  </div>
</template>

<script>
import { ObraService } from '@/services/obraService'
import { Obra } from '@/types'

export default {
  name: 'ObraDetalle',
  data() {
    return {
      obra: null,
      cargando: false
    }
  },
  async mounted() {
    await this.cargarObra()
  },
  methods: {
    async cargarObra() {
      const id = this.$route.params.id
      if (!id) {
        this.$router.push({ name: 'obras-lista' })
        return
      }

      this.cargando = true
      try {
        this.obra = await ObraService.getById(Number(id))
      } catch (error) {
        console.error('Error cargando obra:', error)
        this.$toast({
          component: { template: '<div>Error al cargar la obra</div>' },
          props: { variant: 'danger' },
        })
        this.obra = null
      } finally {
        this.cargando = false
      }
    },

    getEstadoVariant(estado) {
      const variants = {
        'En Progreso': 'primary',
        'Completada': 'success',
        'Pendiente': 'warning',
        'Cancelada': 'danger',
        'Pausada': 'secondary'
      }
      return variants[estado] || 'secondary'
    },

    formatearFecha(fecha) {
      if (!fecha) return '-'
      return new Date(fecha).toLocaleDateString('es-ES')
    },

    formatearFechaHora(fecha) {
      if (!fecha) return '-'
      return new Date(fecha).toLocaleString('es-ES')
    },

    volver() {
      this.$router.push({ name: 'obras-lista' })
    },

    editarObra() {
      if (this.obra) {
        this.$router.push({ name: 'obras-editar', params: { id: this.obra.idObra.toString() } })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.obra-detalle {
  padding: 1.5rem;
}

.content-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e9ecef;
}

.content-header-title {
  font-size: 2rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
  color: #2c3e50;
}

.content-header-subtitle {
  color: #6c757d;
  margin-bottom: 0;
}

.form-control-plaintext {
  padding: 0.375rem 0;
  margin-bottom: 0;
  line-height: 1.5;
  color: #495057;
  background-color: transparent;
  border: solid transparent;
  border-width: 1px 0;
}

.badge {
  font-size: 0.875rem;
  font-weight: 500;
}

.card {
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
}

.card-title {
  font-size: 1.25rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 1rem;
}

@media (max-width: 768px) {
  .obra-detalle {
    padding: 1rem;
  }
  
  .content-header {
    flex-direction: column;
    align-items: stretch;
    
    .content-header-right {
      margin-top: 1rem;
    }
  }
  
  .content-header-title {
    font-size: 1.5rem;
  }
}
</style>
